import React, { useContext, useState } from 'react';
import { T, useGT } from 'gt-react';
import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { useToast } from 'src/context/ToastContext';
import { useImportOrderfulTransaction } from '@/api/rpc/hooks/edi';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';

interface ImportOrderDialogProps {
  /**
   * Rendered in place of the default outline button. Use on pages whose header
   * already has its own button styling.
   */
  className?: string;
}

/**
 * Import an Orderful 850/875 transaction by ID, running the same ingestion the
 * poller does. Importing a transaction (or order) that already exists fails.
 *
 * **Renders nothing for non-admins.** The backend rejects them, so the gate
 * lives here rather than at each call site — a page that mounts this can't
 * forget it, and adding a new entry point can't accidentally widen access.
 */
export function ImportOrderDialog({ className }: ImportOrderDialogProps = {}) {
  const gt = useGT();
  const { showToast } = useToast();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const [open, setOpen] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const importOrder = useImportOrderfulTransaction();

  const handleImport = (test = false) => {
    const id = transactionId.trim();
    if (!id || importOrder.isPending) return;

    importOrder.mutate(
      { transactionId: id, test },
      {
        onSuccess: (data) => {
          const shortType = data.transactionType.startsWith('850')
            ? '850'
            : '875';
          const replaced =
            data.replacedDraftCount > 0
              ? ` · replaced ${data.replacedDraftCount} existing draft${
                  data.replacedDraftCount === 1 ? '' : 's'
                }`
              : '';
          showToast({
            // A discarded approved draft leaves a real sales order behind, so
            // that outcome is surfaced as a warning rather than a plain success.
            color: data.warning ? 'warning' : 'success',
            title: gt('Order imported', {
              context: 'toast title after importing an Orderful order',
            }),
            message: `${shortType} · PO ${data.purchaseOrderNumber}${
              data.customerName ? ` · ${data.customerName}` : ''
            } (${data.stream})${replaced}${
              data.warning ? `\n${data.warning}` : ''
            }`,
          });
          setTransactionId('');
          setOpen(false);
        },
        onError: (error) => {
          const message =
            error instanceof Error ? error.message : String(error);
          showToast({
            color: 'error',
            title: gt('Import failed', {
              context: 'toast title when importing an Orderful order fails',
            }),
            message,
            copyText: message,
          });
        },
      },
    );
  };

  if (!isAdmin(currentUser, viewMode)) return null;

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className={className ?? 'gap-1 shrink-0'}
        onClick={() => setOpen(true)}
      >
        <DownloadSimpleIcon size={14} />
        <T context="button to import an order from Orderful by transaction id">
          Import order from Orderful
        </T>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <T context="import order dialog title">
                Import order from Orderful
              </T>
            </DialogTitle>
            <DialogDescription>
              <T context="import order dialog description">
                Enter an Orderful 850 or 875 transaction ID. The order is
                ingested through the same pipeline as the poller. Importing a
                transaction or order that already exists will fail — use Test
                re-import to discard the existing draft and start over.
              </T>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1">
            <Label
              htmlFor="orderful-transaction-id"
              className="text-xs font-medium text-gray-600"
            >
              <T context="label for orderful transaction id input">
                Transaction ID
              </T>
            </Label>
            <Input
              id="orderful-transaction-id"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleImport();
              }}
              placeholder={gt('e.g. 949358075', {
                context: 'placeholder for orderful transaction id input',
              })}
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={importOrder.isPending}
            >
              <T context="cancel button in import order dialog">Cancel</T>
            </Button>
            {/* Discards any existing draft for this order and ingests again
                from scratch, for trialling the same transaction repeatedly. */}
            <Button
              variant="secondary"
              onClick={() => handleImport(true)}
              disabled={!transactionId.trim() || importOrder.isPending}
            >
              {importOrder.isPending ? (
                <T context="test re-import button loading state">Importing…</T>
              ) : (
                <T context="button to discard the existing draft and import the transaction again">
                  Test re-import
                </T>
              )}
            </Button>
            <Button
              onClick={() => handleImport(false)}
              disabled={!transactionId.trim() || importOrder.isPending}
            >
              {importOrder.isPending ? (
                <T context="import order button loading state">Importing…</T>
              ) : (
                <T context="import order confirm button">Import</T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
